import { c } from 'ttag';

import { BadgeCategory } from '@freelancelabs/components';

import { DashboardBlockedReason, MembershipDisplayStatus } from '../../graphql/__generated__/schema';

/**
 * Why a dashboard habilitation opens nothing today: PCG-106.
 *
 * **Attention, `opens` false never means « pas accordée ».** The line is there, it survives,
 * and that survival is deliberate: it is what answers « qui l'avait » once the entry
 * has left the catalogue. Hiding a blocked habilitation would answer « on ne vous
 * l'a jamais accordé » to somebody asking why their dashboard stopped opening : so
 * every held entry is rendered, and the blocked ones say which of the three
 * impediments holds them.
 *
 * **Three reasons, three sentences, because they call for three different gestures.**
 * The API separates them on exactly that criterion (what the reader would *do*) and
 * folding them into « ce tableau de bord n'est pas disponible » would put the
 * operator in front of a fact with no way out:
 *
 * - `ARCHIVED`: somebody withdrew the entry from the account's catalogue after it
 *   was granted. Nothing un-archives an entry today, so the way out is a **new**
 *   declaration, not a repair of this one;
 * - `MODULE_NOT_ENABLED`: the module is closed on the account. This is a
 *   configuration left half-done rather than an anomaly: granting before opening is
 *   the normal order of the two gestures;
 * - `PERIMETER_NOT_DECLARED`: the catalogue entry carries no locked parameter, so
 *   the token maker refuses to sign. It is the catalogue that has to be corrected,
 *   not the habilitation.
 */
export interface BlockedNotice {
    /** The short qualifier, beside the entry. It names the state, never the gesture. */
    tag: string;
    /** What is true, then what to do about it. Never « une erreur est survenue ». */
    sentence: string;
    /**
     * `warning` throughout: a blocked habilitation is a configuration to finish, not
     * an incident, and it is not the operator's mistake either. `danger` here would
     * send them hunting for a breakdown that has not happened.
     */
    tone: BadgeCategory;
}

/** How a blocked habilitation reads, or null when it opens. */
export const blockedNotice = (reason: DashboardBlockedReason | null | undefined): BlockedNotice | null => {
    switch (reason) {
        case DashboardBlockedReason.Archived:
            return {
                tag: c('Dashboard habilitation').t`Entry archived`,
                sentence: c('Dashboard habilitation')
                    .t`The entry left this account's catalogue after it was granted. The habilitation survives on purpose — it is what answers « who had it » — and it opens nothing. Nothing un-archives an entry today: declare the same dashboard again on this account, grant the new entry, then withdraw this one.`,
                tone: 'warning',
            };
        case DashboardBlockedReason.ModuleNotEnabled:
            return {
                tag: c('Dashboard habilitation').t`Module closed`,
                sentence: c('Dashboard habilitation')
                    .t`The « Tableau de bord » module is not open on this account. Two distinct things: opening the module gives the account the section, granting a dashboard gives that section its content — so this is a configuration left half-done, not an anomaly. The module is opened from the users list filtered on this account.`,
                tone: 'warning',
            };
        case DashboardBlockedReason.PerimeterNotDeclared:
            return {
                tag: c('Dashboard habilitation').t`Perimeter not declared`,
                sentence: c('Dashboard habilitation')
                    .t`The catalogue entry declares no locked parameter carrying the account, so the token maker refuses to sign: signing without it would show the whole dashboard to whoever holds the link. It is the catalogue entry that has to be completed — today that means archiving it and declaring it again with its parameter.`,
                tone: 'warning',
            };
        default:
            return null;
    }
};

/**
 * **Attention, the fourth condition, and the API deliberately left it out of the
 * enumeration.** The token maker also requires an **active membership**
 * (`membership.status = 'active'`), but that fact already sits on the object
 * carrying the habilitation: `PortalUserMembership.status`, printed two cards above
 *, and repeating it inside `blockedReason` would be a second definition of one
 * rule. So it is read here, from the status the sheet already shows, and said once
 * for the whole account block rather than stamped on every entry.
 *
 * Null when the access is open. Two sentences rather than one, because the two cases
 * are held by different things: the person's account, or this access.
 */
export const inactiveAccessSentence = (status: MembershipDisplayStatus | null | undefined): string | null => {
    switch (status) {
        // Both derive from `membership.status = 'active'`; never having signed in
        // does not close anything.
        case MembershipDisplayStatus.Active:
        case MembershipDisplayStatus.ActiveNeverLoggedIn:
        case null:
        case undefined:
            return null;
        case MembershipDisplayStatus.Disabled:
            return c('Dashboard habilitation')
                .t`This person's account is disabled, so they get in nowhere and none of what they hold here opens. The habilitations stay granted and wait.`;
        default:
            return c('Dashboard habilitation')
                .t`This access is not active, and the token maker requires an active membership: nothing here opens until it is. The habilitations stay granted and wait.`;
    }
};
